"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { useApolloClient } from "@apollo/client/react";

/**
 * Reclaims unreachable normalized entries from the Apollo InMemoryCache.
 *
 * Why this exists: the browser CRM cache (`src/lib/apollo-client.ts`) is a bare
 * `new InMemoryCache()` with no `typePolicies` and no eviction. Every entity
 * list view runs a `*Collection` query whose result is normalized under a cache
 * key derived from its filter/view args. The default `keyArgs` means each
 * distinct saved view / filter combination becomes its OWN cache entry. Nothing
 * in hub ever calls `cache.gc()`, so as a heavy CRM user switches entities,
 * views, and filters, the normalized store grows monotonically for the whole
 * session — entries from views the user navigated away from are retained
 * forever even though no mounted query references them anymore.
 *
 * `InMemoryCache.gc()` is safe: it only collects objects that are NOT reachable
 * from any active (mounted/watched) query's root. Data the user is currently
 * looking at is reachable and is never dropped. So running gc when the route
 * changes (the previous page's watchQueries have unsubscribed) and on an idle
 * cadence reclaims exactly the abandoned-view entries without touching anything
 * on screen — and without changing pagination/merge semantics, which the CRM
 * list hooks depend on.
 *
 * Mounted once inside the ApolloProvider (see `providers.tsx`).
 */
export function ApolloCacheGc(): null {
  const client = useApolloClient();
  const pathname = usePathname();
  // Skip the gc on the very first render (nothing abandoned yet).
  const isFirst = useRef(true);

  // Reclaim entries left behind by the page we just navigated away from.
  useEffect(() => {
    if (isFirst.current) {
      isFirst.current = false;
      return;
    }
    // Let the new route's queries mount/subscribe first so their data stays
    // reachable, then collect what the old route abandoned.
    const id = setTimeout(() => {
      try {
        client.cache.gc();
      } catch {
        // gc is best-effort housekeeping — never surface to the user.
      }
    }, 0);
    return () => clearTimeout(id);
  }, [pathname, client]);

  // Idle sweep: also reclaim when the tab is backgrounded and on a slow
  // interval, so a user who stays on one heavy page for a long time (e.g. a
  // dashboard left open) still releases churned background-refetch garbage.
  useEffect(() => {
    const sweep = () => {
      try {
        client.cache.gc();
      } catch {
        // best-effort
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") sweep();
    };
    document.addEventListener("visibilitychange", onVisibility);
    const interval = window.setInterval(sweep, 5 * 60 * 1000); // 5 min
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.clearInterval(interval);
    };
  }, [client]);

  return null;
}
